import { useCallback, useMemo } from 'react';
import { Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { PersonsV3 } from '@frontend/api-person';
import { SMSDataSharedConstants, SMSDataV3 } from '@frontend/api-sms-data';
import { UsersQueries } from '@frontend/api-users';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInboxMutePolicy } from '@frontend/inbox-mute-policy';
import { getFullName } from '@frontend/name';
import { formatPhoneNumber, formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppFlagStore } from '@frontend/shared';
import { useTaskCitation, useGetTaskCenterCustomizationFlagValue } from '@frontend/task-center-scope';
import { sentry } from '@frontend/tracking';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { ChatTypes, ContextMenuActions, useAlert, useModalControl } from '@frontend/design-system';
import { SMSContextActions } from '../types';
import { copyImage, downloadImage } from '../utils/media-interactions';
import { shouldPromptForSmsDeleteConfirmation } from '../utils/sms-delete-preferences';

type UseSMSContextActionsArgs = {
  sms: SMS;
  hiddenActions?: SMSContextActions[];
  openTagSelection?: () => void;
  onResend?: () => void;
  /** Fallback personId from thread's contact when sms.personId is empty */
  threadPersonId?: string;
};

export const useSMSContextActions = ({
  sms,
  hiddenActions,
  openTagSelection,
  onResend,
  threadPersonId,
}: UseSMSContextActionsArgs) => {
  const { t } = useTranslation('thread-body');
  const alert = useAlert();
  const user = getUser();
  const { isHidden } = useInboxMutePolicy();
  const { featureFlags } = useAppFlagStore();
  const { modalProps, openModal, closeModal } = useModalControl();

  const aclEnforcementEnabled = !!featureFlags[sms.locationId]?.get('nwx:messaging-inbox-fe-acl-enforcement')?.value;
  const hasDeleteACL = aclEnforcementEnabled ? hasSchemaACL(sms.locationId, Permission.SMS_INBOX_MESSAGE_DELETE) : true;
  const isSending = Object.entries(SMSDataSharedConstants.SENDING_STATUS_DATA).every(
    ([key, value]) => sms[key as keyof typeof SMSDataSharedConstants.SENDING_STATUS_DATA] === value
  );
  const isDeleted = !!sms.deletedAt;

  const { getFlagByLocation } = useGetTaskCenterCustomizationFlagValue();

  const taskCenterCFEnabled = getFlagByLocation(sms.locationId);
  const { setMessageCitation } = useTaskCitation();

  // Use sms.personId if available, otherwise fallback to thread's contact personId
  const resolvedPersonId = sms.personId || threadPersonId || '';
  const { data: person } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      locationIds: [sms.locationId],
      personId: resolvedPersonId,
    },
    { enabled: !!resolvedPersonId }
  );
  const locationUsersQueries = UsersQueries.useGetMultiLocationsUsers({
    locationIds: [sms.locationId],
    params: { showChildren: true },
  });
  const locationUsers = useMemo(() => locationUsersQueries.flatMap(({ data }) => data || []), [locationUsersQueries]);
  const sender = useMemo(
    () => locationUsers.find((user) => user.UserID === sms.createdBy),
    [locationUsers, sms.createdBy]
  );

  const { mutateAsync: deleteSms } = SMSDataV3.Mutations.useDeleteSMSMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Message deleted'));
      },
      onError: () => {
        alert.error(t('Error deleting message'));
      },
    },
  });

  const handleDelete = useCallback(async () => {
    if (shouldPromptForSmsDeleteConfirmation()) {
      openModal();
    } else {
      await deleteSms({
        smsId: sms.id,
        locationId: sms.locationId,
        userId: user?.userID ?? '',
        createdAt: sms.createdAt,
        _otherOptions: {
          threadId: sms.threadId,
        },
      });
    }
  }, [deleteSms, openModal, sms.createdAt, sms.id, sms.locationId, sms.threadId, user?.userID]);

  const handleDeleteConfirmation = useCallback(async () => {
    await deleteSms({
      smsId: sms.id,
      locationId: sms.locationId,
      userId: user?.userID ?? '',
      createdAt: sms.createdAt,
      _otherOptions: {
        threadId: sms.threadId,
      },
    });
    closeModal();
  }, [closeModal, deleteSms, sms.createdAt, sms.id, sms.locationId, sms.threadId, user?.userID]);

  const handleCopySMSBody = useCallback(() => {
    if (sms.body) {
      navigator.clipboard.writeText(sms.body);
      alert.success(t('Message Copied'));
    } else {
      alert.error(t('No text to copy'));
    }
  }, [alert, sms.body, t]);
  const handleCopyImage = useCallback(
    async (mediaId: string) => {
      try {
        const mediaItem = sms.media.find((item) => item.mediaId === mediaId);
        await copyImage(mediaId, mediaItem?.mimetype, sms.locationId);
        alert.success(t('Image Copied'));
      } catch (err) {
        alert.error(t('Error copying image'));
        sentry.error({
          error: err,
          topic: 'messages',
          team: 'fe-messaging',
          severityLevel: 'error',
          addContext: {
            name: 'Message object & media item',
            context: {
              sms,
              mediaId,
              errMessage: 'Error copying image from message',
            },
          },
        });
      }
    },
    [alert, sms, t]
  );
  const handleDownloadImage = useCallback(
    (mediaId: string, mediaType: 'image' | 'file' | 'video' = 'image') => {
      const mediaItem = sms.media.find((item) => item.mediaId === mediaId);
      const successMessage = {
        image: t('Image Downloaded'),
        file: t('File Downloaded'),
        video: t('Video Downloaded'),
      }[mediaType];
      const errorMessage = {
        image: t('Error downloading image'),
        file: t('Error downloading file'),
        video: t('Error downloading video'),
      }[mediaType];
      try {
        downloadImage({
          mediaId,
          filename: mediaItem?.filename ?? `${sms.personPhone}-${t('image-download')}`,
          mimetype: mediaItem?.mimetype,
          locationId: sms.locationId,
        });
        alert.success(successMessage);
      } catch (err) {
        alert.error(errorMessage);
        sentry.error({
          error: err,
          topic: 'messages',
          team: 'fe-messaging',
          severityLevel: 'error',
          addContext: {
            name: 'Message object & media item',
            context: {
              sms,
              mediaId,
              errMessage: `Error downloading ${mediaType} from message`,
            },
          },
        });
      }
    },
    [alert, sms, t]
  );

  const { isLoading: isResending, mutateAsync: resendMessage } = SMSDataV3.Mutations.useResendSMSMutation({
    options: {
      onSuccess: () => {
        onResend?.();
      },
      onError: () => {
        alert.error(t('Error resending message'));
      },
    },
    optimisticUpdate: true,
  });

  const retryableStatuses = [Status.STATUS_ERROR, Status.STATUS_NOT_SENT];
  const canRetry = !isResending && retryableStatuses.includes(sms.status) && !hiddenActions?.includes('retry');

  const handleRetry = useCallback(
    async () =>
      resendMessage({
        smsId: sms.id,
        threadId: sms.threadId,
        locationId: sms.locationId,
      }),
    [resendMessage, sms.id, sms.locationId, sms.threadId]
  );

  const getActions = useCallback(
    (mediaId?: string): { primaryActions: ChatTypes.PrimaryAction[]; moreActions: ContextMenuActions } => {
      const result: { primaryActions: ChatTypes.PrimaryAction[]; moreActions: ContextMenuActions } = {
        primaryActions: [],
        moreActions: [],
      };

      // Hide all actions for deleted messages
      if (isDeleted) {
        return result;
      }

      if (taskCenterCFEnabled && !hiddenActions?.includes('tasks') && !isHidden('tasks')) {
        result.primaryActions.push({
          label: t('Create Task'),
          icon: 'task-small',
          onClick: () => {
            let personName = formatPhoneNumber(sms.personPhone, true);
            if (sender) {
              personName = getFullName(sender);
            } else if (person) {
              personName = getFullName(person);
            }
            setMessageCitation({
              sourceId: sms.id,
              source: {
                description: sms.body,
                phone_no: sms.personPhone,
                created_at: sms.createdAt,
                user_id: sms.createdBy,
                patient_id: resolvedPersonId,
                direction: sms.direction,
                thread_id: sms.threadId,
                locationPhone: formatPhoneNumberE164(sms.locationPhone),
                media_ids: sms.media.map((mediaItem) => ({ mediaId: mediaItem.mediaId })),
                smsLocationId: sms.locationId,
              },
              personName,
              locationId: sms.locationId,
            });
          },
          trackingId: 'task-center-message-action-create-task',
        });
      }

      if (openTagSelection && !hiddenActions?.includes('tag') && !isHidden('tags')) {
        result.primaryActions.push({
          label: t('Tag Message'),
          icon: 'label',
          onClick: openTagSelection,
          trackingId: `${InboxPrefixes.Tag}-tag-message`,
        });
      }

      if (!mediaId && !hiddenActions?.includes('copy')) {
        result.moreActions.push({
          label: t('Copy Message'),
          Icon: () => <Icon name='copy' size={18} />,
          onClick: handleCopySMSBody,
          trackingId: `${InboxPrefixes.Thread}-message-action-copy`,
        });
      }

      if (mediaId) {
        if (!hiddenActions?.includes('copy-image')) {
          result.moreActions.push({
            label: t('Copy Image'),
            Icon: () => <Icon name='copy' size={18} />,
            onClick: () => handleCopyImage(mediaId),
            trackingId: `${InboxPrefixes.Thread}-message-action-copy-image`,
          });
        }
        if (!hiddenActions?.includes('download-image')) {
          result.moreActions.push({
            label: t('Download Image'),
            Icon: () => <Icon name='download' size={18} />,
            onClick: () => handleDownloadImage(mediaId),
            trackingId: `${InboxPrefixes.Thread}-message-action-download-image`,
          });
        }
      }

      if (canRetry && !hiddenActions?.includes('retry')) {
        result.moreActions.push({
          label: t('Retry Send'),
          Icon: () => <Icon name='update' size={18} />,
          onClick: handleRetry,
          trackingId: `${InboxPrefixes.Thread}-message-action-retry`,
        });
      }

      if (!isSending && !hiddenActions?.includes('delete') && !isHidden('sms-deletion')) {
        result.moreActions.push({
          label: t('Delete from Thread'),
          Icon: () => <Icon name='trash' size={18} />,
          onClick: handleDelete,
          disabled: !hasDeleteACL,
          hoverLabel: !hasDeleteACL ? t('You do not have permission to delete messages.') : undefined,
        });
      }

      return result;
    },
    [
      openTagSelection,
      hiddenActions,
      taskCenterCFEnabled,
      canRetry,
      isDeleted,
      isSending,
      t,
      sms.personPhone,
      sms.id,
      sms.body,
      sms.createdAt,
      sms.createdBy,
      sms.personId,
      sms.direction,
      sms.locationId,
      sms.locationPhone,
      sms.threadId,
      resolvedPersonId,
      sender,
      person,
      setMessageCitation,
      handleCopySMSBody,
      handleCopyImage,
      handleDownloadImage,
      handleRetry,
      handleDelete,
      hasDeleteACL,
      isHidden,
    ]
  );

  return {
    getActions,
    handleDownloadImage,
    deleteConfirmationModalProps: modalProps,
    handleDeleteConfirmation,
  };
};
